
import React from 'react';
import { Loader2 } from 'lucide-react';
import Modal from './Modal';

export type DialogType = 'alert' | 'confirm' | 'prompt' | 'info';

interface DialogProps {
  isOpen: boolean;
  type: DialogType;
  title: string;
  message: React.ReactNode;
  onConfirm: (value?: string) => void;
  onCancel: () => void;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  type,
  title,
  message,
  onConfirm,
  onCancel,
  defaultValue = '',
  confirmText = 'OK',
  cancelText = 'キャンセル',
  isDestructive = false,
  isLoading = false
}) => {
  const [inputValue, setInputValue] = React.useState(defaultValue);

  React.useEffect(() => {
    if (isOpen) setInputValue(defaultValue);
  }, [isOpen, defaultValue]);

  const handleConfirm = () => {
    if (isLoading) return;
    if (type === 'prompt') {
      onConfirm(inputValue);
    } else {
      onConfirm();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={type === 'alert' || type === 'info' ? handleConfirm : onCancel} 
      maxWidth="max-w-sm" 
      zIndex="z-[10005]"
    >
      <div className="text-center">
        <h3 className="text-lg font-black text-slate-800 mb-3">{title}</h3>
        <div className="text-sm text-slate-500 mb-8 whitespace-pre-wrap leading-relaxed font-bold">
          {message}
        </div>

        {type === 'prompt' && (
          <input
            type="text"
            className="w-full border border-slate-300 rounded-xl p-3 mb-6 focus:ring-4 focus:ring-blue-100 focus:border-blue-500 outline-none font-bold text-slate-700 text-sm transition-all"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            autoFocus
          />
        )}

        <div className="flex gap-3 mt-2">
          {(type === 'confirm' || type === 'prompt') && (
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 py-3.5 bg-slate-100 text-slate-600 font-black rounded-xl hover:bg-slate-200 transition-all text-sm disabled:opacity-50"
            >
              {cancelText}
            </button>
          )}
          
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`flex-1 py-3.5 font-black rounded-xl text-white shadow-lg transition-all active:scale-95 text-sm flex items-center justify-center ${
              (isDestructive || type === 'alert') 
                ? 'bg-red-500 hover:bg-red-600 shadow-red-100' 
                : 'bg-blue-600 hover:bg-blue-700 shadow-blue-100'
            } disabled:bg-slate-400`}
          >
            {isLoading && <Loader2 size={16} className="animate-spin mr-2" />}
            {isLoading ? '処理中...' : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default Dialog;
